import { webApi } from '@mi-front/api-clients';
import { Box, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';
import { FRAGMENT_CARD_PADDING_X } from '../constants';
import { FragmentCardDurationPill } from './FragmentCardDurationPill';
import { FragmentCardStatuses, hasStatusesContent } from './FragmentCardStatuses';
import { FragmentCardTimePill } from './FragmentCardTimePill';
import { FragmentMediaPill } from './FragmentMediaPill';

const overlayRowBaseSx: SxProps<Theme> = {
  position: 'absolute',
  px: FRAGMENT_CARD_PADDING_X,
  py: 1,
  display: 'flex',
  alignItems: 'center',
  zIndex: 1,
};

/**
 * Renders fragment overlay pills (time, statuses, duration) on top of
 * any media content (waveform, poster, etc.) using flex-based absolute overlays.
 */
export function FragmentCardMediaOverlay({
  fragment,
  children,
  sx,
}: {
  fragment: webApi.FragmentListItemDto;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', ...sx }}>
      {children}
      {/* Top row: time pill left, statuses right */}
      <Box
        sx={{
          ...overlayRowBaseSx,
          top: 0,
          left: 0,
          right: 0,
          justifyContent: 'space-between',
        }}
      >
        <FragmentCardTimePill fragment={fragment} />
        {hasStatusesContent({
          uploadStatuses: fragment.uploadStatuses,
          externalDelivery: fragment.externalDelivery,
          publishedPlatforms: fragment.publishedPlatforms,
          externalDeliveryStartedAt: fragment.externalDeliveryStartedAt,
          contentAiCreatedAt: fragment.contentAiCreatedAt,
        }) ? (
          <FragmentMediaPill>
            <FragmentCardStatuses
              uploadStatuses={fragment.uploadStatuses}
              externalDelivery={fragment.externalDelivery}
              publishedPlatforms={fragment.publishedPlatforms}
              externalDeliveryStartedAt={fragment.externalDeliveryStartedAt}
              contentAiCreatedAt={fragment.contentAiCreatedAt}
            />
          </FragmentMediaPill>
        ) : null}
      </Box>
      {/* Bottom right: duration pill */}
      <Box
        sx={{
          ...overlayRowBaseSx,
          bottom: 0,
          right: 0,
          justifyContent: 'flex-end',
        }}
      >
        <FragmentCardDurationPill fragment={fragment} />
      </Box>
    </Box>
  );
}
